import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Phone, Mail, MapPin, Clock, Send, Zap, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSectionImproved = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast({
        title: '¡Mensaje enviado!',
        description: 'Nos pondremos en contacto contigo pronto.',
      });
      setFormData({ name: '', phone: '', message: '' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Hubo un problema al enviar el mensaje. Por favor, inténtalo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section id="contact" className="bg-[#0A1628] py-24 md:py-32 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-40 left-20 w-96 h-96 bg-[#60A5FA] rounded-full filter blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#3B82F6] rounded-full filter blur-3xl" />
      </div>

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-7xl mx-auto px-6 relative z-10"
      >
        {/* Section Number */}
        <motion.div variants={itemVariants} className="mb-8">
          <span className="text-[#60A5FA] text-lg font-mono">06</span>
        </motion.div>

        {/* Section Title */}
        <motion.h2 variants={itemVariants} className="text-5xl md:text-6xl font-bold text-white mb-6">
          Contáctanos
        </motion.h2>

        <motion.p variants={itemVariants} className="text-white/70 text-lg mb-16 max-w-2xl">
          Estamos listos para atender tu vehículo. Contáctanos por WhatsApp o llena el formulario
        </motion.p>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info - 2 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-2 space-y-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-gradient-to-br from-[#60A5FA]/10 to-[#3B82F6]/10 border border-[#60A5FA]/30 rounded-2xl p-6">
                <Zap className="w-8 h-8 text-[#60A5FA] mb-3" />
                <p className="text-white font-bold text-2xl">&lt; 15 min</p>
                <p className="text-white/60 text-sm">Respuesta WhatsApp</p>
              </div>
              <div className="bg-gradient-to-br from-[#60A5FA]/10 to-[#3B82F6]/10 border border-[#60A5FA]/30 rounded-2xl p-6">
                <Users className="w-8 h-8 text-[#60A5FA] mb-3" />
                <p className="text-white font-bold text-2xl">100+</p>
                <p className="text-white/60 text-sm">Clientes satisfechos</p>
              </div>
            </div>

            {/* Contact Details */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-[#60A5FA]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#60A5FA]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm mb-1">Teléfono / WhatsApp</p>
                  <p className="text-white text-xl font-semibold">0958646225</p>
                  <p className="text-[#60A5FA] text-sm mt-1">Disponible 24/7</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-[#60A5FA]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#60A5FA]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm mb-1">Ubicación</p>
                  <p className="text-white text-lg font-semibold">Pichincha, Ecuador</p>
                  <p className="text-white/70 text-sm">(Servicio a domicilio disponible)</p>
                  <p className="text-[#60A5FA] text-sm mt-1">Cobertura: Toda la provincia</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-14 h-14 bg-[#60A5FA]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#60A5FA]" />
                </div>
                <div>
                  <p className="text-white/50 text-sm mb-1">Horario de atención</p>
                  <p className="text-white text-lg font-semibold">Lunes a Sábado</p>
                  <p className="text-white/70">8:00 AM - 6:00 PM</p>
                  <p className="text-white text-lg font-semibold mt-2">Domingo</p>
                  <p className="text-white/70">9:00 AM - 2:00 PM</p>
                </div>
              </div>
            </div>

            {/* Map */}
            <motion.div
              variants={itemVariants}
              className="w-full h-80 bg-[#0F1D33]/50 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d510562.8487071147!2d-78.84457954999999!3d-0.2298500000000001!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x91d59a4002427c9f%3A0x6b5b6b6b6b6b6b6b!2sPichincha%2C%20Ecuador!5e0!3m2!1ses!2sec!4v1234567890123!5m2!1ses!2sec"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación Pichincha, Ecuador"
              />
            </motion.div>
          </motion.div>

          {/* Contact Form - 3 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-3">
            <div className="bg-gradient-to-br from-[#0F1D33]/80 to-[#0A1628]/60 backdrop-blur-2xl border border-white/20 rounded-3xl p-10 shadow-2xl">
              <h3 className="text-3xl font-bold text-white mb-3">Envíanos un mensaje</h3>
              <p className="text-white/60 mb-8">Te responderemos en menos de 24 horas</p>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="text-white/70 text-sm mb-2 block font-medium">Nombre completo</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-[#0A1628]/60 border-white/20 text-white focus:border-[#60A5FA] transition-all h-14 text-lg rounded-xl"
                    placeholder="Juan Pérez"
                  />
                </div>

                <div>
                  <label className="text-white/70 text-sm mb-2 block font-medium">Teléfono / WhatsApp</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="bg-[#0A1628]/60 border-white/20 text-white focus:border-[#60A5FA] transition-all h-14 text-lg rounded-xl"
                    placeholder="0991234567"
                  />
                </div>

                <div>
                  <label className="text-white/70 text-sm mb-2 block font-medium">¿Qué servicio necesitas?</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-[#0A1628]/60 border-white/20 text-white focus:border-[#60A5FA] transition-all resize-none text-lg rounded-xl"
                    placeholder="Cuéntanos qué necesita tu vehículo..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] hover:from-[#3B82F6] hover:to-[#2563EB] text-white text-lg py-7 rounded-2xl transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(96,165,250,0.5)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Enviar mensaje
                    </>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default ContactSectionImproved;
